import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, Eye, ChevronLeft, ChevronRight, PackageCheck, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const SupplierOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const supplierId = user?._id;
      
      if (!supplierId) {
        setLoading(false);
        return;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/get-supplier-orders/${supplierId}`
      );
      setOrders(res.data || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filteredOrders = orders.filter(
    (order) =>
      order._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredOrders.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const currentOrders = filteredOrders.slice(startIndex, startIndex + entriesPerPage);

  const SkeletonRow = () => (
    <tr className="border-b border-gray-100">
      {[...Array(6)].map((_, i) => (
        <td key={i} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case "Delivered": return "bg-green-100 text-green-600";
      case "Shipped": return "bg-blue-100 text-blue-600";
      case "Pending": return "bg-yellow-100 text-yellow-600";
      case "Cancelled": return "bg-red-100 text-red-600";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/orders/update-order-status/${orderId}`, {
        status: newStatus
      });
      fetchOrders();
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status");
    }
  };

  return (
    <section className="min-h-screen p-6 bg-[#EEF3F7]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
          <PackageCheck className="text-[#fba93d] w-7 h-7" />
          Orders
        </h1>
        <button
          onClick={fetchOrders}
          className="flex items-center gap-2 bg-[#ea8d2a] text-white px-4 py-2 rounded-lg hover:bg-orange-600"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:outline-none focus:ring-1 focus:ring-[#ea8d2a]"
          />
          <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">#</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Order ID</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Customer</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Total (Your Share)</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Update Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => <SkeletonRow key={i} />)
              ) : currentOrders.length > 0 ? (
                currentOrders.map((order, index) => (
                  <tr key={order._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                    <td className="px-4 py-4 text-gray-500 font-medium">{startIndex + index + 1}</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{order._id.slice(-6).toUpperCase()}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {order.user ? `${order.user.firstName} ${order.user.lastName}` : "--"}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">₹{order.totalAmount?.toFixed(2) || "0.00"}</td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <select 
                        value={order.status}
                        onChange={(e) => handleStatusChange(order._id, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#ea8d2a]"
                      >
                        <option value="New">New</option>
                        <option value="Accepted">Accepted</option>
                        <option value="Processing">Processing</option>
                        <option value="Packed">Packed</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No orders found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && filteredOrders.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredOrders.length)} of {filteredOrders.length} orders
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SupplierOrders;
